/**
 * PPM-Based Price Change Calculator
 *
 * Calculates expected price changes based on the official F1 Fantasy formula:
 * - Price changes are determined by 3-race rolling average PPM
 * - PPM = Points Per Million = points / price
 * - Thresholds: 0.6, 0.9, 1.2 determine the price change bucket
 *
 * Official Rules (as of Japan 2025):
 * - Tier A (>=$19M): Smaller swings of -$0.3, -$0.1, +$0.1, +$0.3
 * - Tier B (<$19M): Larger swings of -$0.6, -$0.5, -$0.2, $0.0, +$0.2, +$0.6
 * - "Two-thirds of the price change has already been determined by previous races"
 */

// Import constants from single source of truth
import {
  applyPriceFloorToTierBProbabilities,
  determineTier,
  PPM_THRESHOLDS,
  type SeasonPricing,
  TIER_A_COLUMNS,
  TIER_A_PRICE_CHANGES,
  TIER_B_COLUMNS,
  TIER_B_POOR_THRESHOLD,
  TIER_B_PRICE_CHANGES,
  type TierAColumn,
  type TierBColumn,
} from "./constants";

// Re-export constants for backwards compatibility
export {
  PPM_THRESHOLDS,
  type SeasonPricing,
  TIER_A_COLUMNS,
  TIER_A_PRICE_CHANGES,
  TIER_B_COLUMNS,
  TIER_B_POOR_THRESHOLD,
  TIER_B_PRICE_CHANGES,
  type TierAColumn,
  type TierBColumn,
};

// ============================================================================
// Types
// ============================================================================

export interface PPMInput {
  /** Points scored in race R-2 (two races ago) */
  pointsRMinus2: number | null;
  /** Price at race R-2 */
  priceRMinus2: number | null;
  /** Points scored in race R-1 (last race) */
  pointsRMinus1: number | null;
  /** Price at race R-1 */
  priceRMinus1: number | null;
  /** Predicted points for current/upcoming race */
  predictedPoints: number;
  /** Current driver price */
  currentPrice: number;
}

export interface PPMPriceChangeResult {
  /** Average PPM across available races */
  avgPPM: number;
  /** Expected price change (weighted average based on probabilities) */
  expectedPriceChange: number;
  /** Expected price change bucket (e.g., "+0.3", "-0.1") */
  expectedPriceChangeBucket: string;
  /** Probability distribution across all price change buckets */
  probabilities: Record<string, number>;
  /** Confidence in the prediction (0-1) based on available historical data */
  confidence: number;
}

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Calculate PPM (Points Per Million)
 *
 * @param points - Points scored
 * @param price - Price in millions
 * @returns PPM value
 */
export function calculatePPM(points: number, price: number): number {
  if (price <= 0) return 0;
  return points / price;
}

/**
 * Calculate 3-race average PPM
 *
 * R-2 and R-1 are actual (if available), R-0 is predicted.
 * The formula weighs all three races equally per official rules.
 *
 * @param input - PPM input with historical and predicted data
 * @returns Average PPM across available data points
 */
export function calculate3RaceAveragePPM(input: PPMInput): number {
  const ppms: number[] = [];

  // R-2 PPM (if available)
  if (
    input.pointsRMinus2 !== null &&
    input.priceRMinus2 !== null &&
    input.priceRMinus2 > 0
  ) {
    ppms.push(calculatePPM(input.pointsRMinus2, input.priceRMinus2));
  }

  // R-1 PPM (if available)
  if (
    input.pointsRMinus1 !== null &&
    input.priceRMinus1 !== null &&
    input.priceRMinus1 > 0
  ) {
    ppms.push(calculatePPM(input.pointsRMinus1, input.priceRMinus1));
  }

  // R-0 PPM (predicted, always use current price)
  if (input.currentPrice > 0) {
    ppms.push(calculatePPM(input.predictedPoints, input.currentPrice));
  }

  if (ppms.length === 0) return 0;

  return ppms.reduce((sum, ppm) => sum + ppm, 0) / ppms.length;
}

// Re-export the shared season-aware tier helper (single source of truth)
export { determineTier };

/**
 * Get price change bucket from PPM for Tier A
 *
 * @param avgPPM - Average PPM value
 * @returns Object with bucket string and numeric change value
 */
function getTierAPriceChange(avgPPM: number): {
  bucket: string;
  change: number;
} {
  if (avgPPM >= PPM_THRESHOLDS.GREAT) {
    return { bucket: "+0.3", change: TIER_A_PRICE_CHANGES.GREAT };
  }
  if (avgPPM >= PPM_THRESHOLDS.GOOD) {
    return { bucket: "+0.1", change: TIER_A_PRICE_CHANGES.GOOD };
  }
  if (avgPPM >= PPM_THRESHOLDS.POOR) {
    return { bucket: "-0.1", change: TIER_A_PRICE_CHANGES.POOR };
  }
  return { bucket: "-0.3", change: TIER_A_PRICE_CHANGES.TERRIBLE };
}

/**
 * Get price change bucket from PPM for Tier B
 *
 * Standard Tier B buckets: +0.6 (great), +0.2 (good), -0.2 (poor), -0.6 (terrible)
 *
 * @param avgPPM - Average PPM value
 * @returns Object with bucket string and numeric change value
 */
function getTierBPriceChange(avgPPM: number): {
  bucket: string;
  change: number;
} {
  if (avgPPM >= PPM_THRESHOLDS.GREAT) {
    return { bucket: "+0.6", change: TIER_B_PRICE_CHANGES.GREAT };
  }
  if (avgPPM >= PPM_THRESHOLDS.GOOD) {
    return { bucket: "+0.2", change: TIER_B_PRICE_CHANGES.GOOD };
  }
  if (avgPPM >= TIER_B_POOR_THRESHOLD) {
    return { bucket: "-0.2", change: TIER_B_PRICE_CHANGES.POOR };
  }
  return { bucket: "-0.6", change: TIER_B_PRICE_CHANGES.TERRIBLE };
}

/**
 * Normal CDF approximation using Abramowitz and Stegun formula
 *
 * @param x - Value to evaluate
 * @param mean - Mean of the distribution
 * @param stddev - Standard deviation
 * @returns Cumulative probability P(X <= x)
 */
function normalCDF(x: number, mean: number, stddev: number): number {
  const z = (x - mean) / stddev;
  // Approximation using error function
  const t = 1 / (1 + 0.2316419 * Math.abs(z));
  const d = 0.3989423 * Math.exp((-z * z) / 2);
  const p =
    d *
    t *
    (0.3193815 +
      t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return z > 0 ? 1 - p : p;
}

/**
 * Calculate price change probabilities based on PPM
 *
 * Since we're predicting R-0 points, there's uncertainty.
 * We model this as probability distributions around the expected PPM
 * using a normal distribution.
 *
 * @param avgPPM - Average PPM value
 * @param tier - "A" or "B" tier
 * @param confidence - Confidence level (0-1)
 * @returns Record of bucket -> probability (0-100)
 */
export function calculatePPMProbabilities(
  avgPPM: number,
  tier: "A" | "B",
  confidence: number,
): Record<string, number> {
  const columns = tier === "A" ? TIER_A_COLUMNS : TIER_B_COLUMNS;
  // Both tiers use the same 3 thresholds for their 4 buckets:
  //   below POOR → terrible, POOR–GOOD → poor, GOOD–GREAT → good, above GREAT → great
  // Tier B uses TIER_B_POOR_THRESHOLD instead of PPM_THRESHOLDS.POOR for the lower split
  const thresholds =
    tier === "A"
      ? [PPM_THRESHOLDS.POOR, PPM_THRESHOLDS.GOOD, PPM_THRESHOLDS.GREAT]
      : [TIER_B_POOR_THRESHOLD, PPM_THRESHOLDS.GOOD, PPM_THRESHOLDS.GREAT];

  // Standard deviation of PPM prediction (higher confidence = lower stddev)
  // Base stddev is 0.3 PPM, scales with confidence
  const stddev = 0.3 * (1 - confidence * 0.5);

  // Calculate cumulative probability for each threshold using normal distribution
  const probs: number[] = [];
  let cumulativeProb = 0;

  for (let i = 0; i < columns.length; i++) {
    let prob: number;
    if (i === 0) {
      // First bucket: probability of being below lowest threshold
      prob = normalCDF(thresholds[0], avgPPM, stddev);
    } else if (i === columns.length - 1) {
      // Last bucket: probability of being above highest threshold
      prob = 1 - normalCDF(thresholds[thresholds.length - 1], avgPPM, stddev);
    } else {
      // Middle buckets: probability between two thresholds
      const lowerThreshold = thresholds[i - 1];
      const upperThreshold = thresholds[i];
      prob =
        normalCDF(upperThreshold, avgPPM, stddev) -
        normalCDF(lowerThreshold, avgPPM, stddev);
    }
    probs.push(Math.max(0, prob)); // Ensure non-negative
    cumulativeProb += Math.max(0, prob);
  }

  // Normalize to ensure they sum to 100, capping at 1%–99% per bucket:
  // no predicted outcome is ever truly certain (DNF = -20 can always shift the avg).
  const result: Record<string, number> = {};
  for (let i = 0; i < columns.length; i++) {
    const raw = Math.round((probs[i] / cumulativeProb) * 100);
    result[columns[i]] = Math.max(1, Math.min(99, raw));
  }

  // Ensure they sum to exactly 100 (fix rounding errors)
  const sum = Object.values(result).reduce((a, b) => a + b, 0);
  if (sum !== 100) {
    // Find the key with max probability and adjust it
    const maxKey = Object.entries(result).reduce((a, b) =>
      b[1] > a[1] ? b : a,
    )[0];
    result[maxKey] += 100 - sum;
  }

  return result;
}

/**
 * Calculate confidence based on available data
 *
 * More historical data = higher confidence
 * R-2 + R-1 = 67% locked in, only R-0 varies
 *
 * @param input - PPM input with historical data
 * @returns Confidence value (0-1)
 */
export function calculateConfidence(input: PPMInput): number {
  let dataPoints = 0;

  if (input.pointsRMinus2 !== null && input.priceRMinus2 !== null) {
    dataPoints++;
  }
  if (input.pointsRMinus1 !== null && input.priceRMinus1 !== null) {
    dataPoints++;
  }

  // 2 historical races = 0.67 confidence (2/3 of data is actual)
  // 1 historical race = 0.5 confidence
  // 0 historical races = 0.33 confidence (all prediction)
  switch (dataPoints) {
    case 2:
      return 0.67;
    case 1:
      return 0.5;
    default:
      return 0.33;
  }
}

/**
 * Calculate expected price delta from probabilities
 *
 * Computes a weighted average of price changes based on their probabilities.
 *
 * @param probabilities - Record of bucket -> probability (0-100)
 * @returns Expected price delta (e.g., +0.15)
 */
export function calculateExpectedPriceDelta(
  probabilities: Record<string, number>,
): number {
  let expectedDelta = 0;

  for (const [change, probability] of Object.entries(probabilities)) {
    const changeValue = Number.parseFloat(change);
    expectedDelta += changeValue * (probability / 100);
  }

  return Math.round(expectedDelta * 100) / 100;
}

/**
 * Main function: Calculate full PPM-based price change prediction
 *
 * This is the primary entry point for price change calculation.
 * It combines all the component functions to produce a complete result.
 *
 * @param input - PPM input with historical and predicted data
 * @param pricing - The season's pricing rules (from getSeasonPricing in
 *   constants.ts). Both the price floor and the tier threshold vary per
 *   season — 2025: floor $4.5M / tier $19M; 2026: floor ~$3.2M / tier $18.5M.
 *   A floor of 0 disables clipping.
 * @returns Complete PPM price change result
 */
export function calculatePPMPriceChange(
  input: PPMInput,
  pricing: SeasonPricing,
): PPMPriceChangeResult {
  const tier = determineTier(input.currentPrice, pricing.tierThreshold);
  const avgPPM = calculate3RaceAveragePPM(input);
  const confidence = calculateConfidence(input);

  // Get expected price change bucket
  const { bucket } =
    tier === "A" ? getTierAPriceChange(avgPPM) : getTierBPriceChange(avgPPM);

  // Calculate probability distribution
  let probabilities = calculatePPMProbabilities(avgPPM, tier, confidence);

  // Handle price floor: apply per-driver floor constraint to Tier B probabilities
  // (clips impossible drops to the actual max achievable drop, e.g. "-0.4" for $4.9M)
  if (tier === "B") {
    probabilities = applyPriceFloorToTierBProbabilities(
      input.currentPrice,
      probabilities,
      pricing.priceFloor,
    );
  }

  // Calculate expected price delta (weighted average)
  const expectedPriceChange = calculateExpectedPriceDelta(probabilities);

  // Adjust bucket if it's now unreachable due to floor clipping
  const effectiveBucket =
    !(bucket in probabilities) || probabilities[bucket] === 0
      ? Object.entries(probabilities).reduce((a, b) => (b[1] > a[1] ? b : a))[0]
      : bucket;

  return {
    avgPPM,
    expectedPriceChange,
    expectedPriceChangeBucket: effectiveBucket,
    probabilities,
    confidence,
  };
}
